import React, { useState } from 'react';

const EMPTY_FORM = { bank_name: '', account_number: '', account_holder: '' };

/**
 * Payout information card component
 * Payouts are made by bank transfer only, so the guardian can store the bank account to receive them.
 * @param {Object} props
 * @param {Object} props.payoutInfo - Payout information object
 * @param {Object} props.translations - Translation strings
 */
const PayoutInfo = ({ payoutInfo, translations }) => {
  const t = translations?.payout_info || {};
  const [bankAccount, setBankAccount] = useState(payoutInfo.bank_account || null);
  const [isEditing, setIsEditing] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [isSaving, setIsSaving] = useState(false);
  const [showSaved, setShowSaved] = useState(false);
  const [error, setError] = useState(null);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('vi-VN').format(amount);
  };

  const formatPercentage = (rate) => {
    return `${(rate * 100).toFixed(0)}%`;
  };

  // Replace placeholders in warning message
  const warningMessage = t.threshold_warning
    ?.replace('{{amount_needed}}', `${formatCurrency(payoutInfo.amount_needed)} ₫`)
    ?.replace('{{minimum_payout}}', `${formatCurrency(payoutInfo.minimum_payout)} ₫`);

  const startEditing = () => {
    setForm(bankAccount || EMPTY_FORM);
    setError(null);
    setShowSaved(false);
    setIsEditing(true);
  };

  const handleChange = (field) => (e) => {
    setForm({ ...form, [field]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSaving(true);
    setError(null);

    try {
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content;
      const response = await fetch('/api/referrals/payout_bank_account', {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken
        },
        body: JSON.stringify(form)
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setBankAccount(data.bank_account);
        setIsEditing(false);
        setShowSaved(true);
      } else {
        setError(data.errors?.join(', ') || 'Failed to save payment info');
      }
    } catch (err) {
      setError('Failed to save payment info. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="card border rounded-3" data-testid="payout-info">
      <div className="card-body p-4">
        <h3 className="h6 fw-bold mb-4">{t.title}</h3>

        <div className="mb-3">
          <div className="d-flex justify-content-between mb-2">
            <span className="text-muted">{t.commission_rate}</span>
            <span className="fw-semibold">{formatPercentage(payoutInfo.commission_rate)}</span>
          </div>
          <div className="d-flex justify-content-between mb-2">
            <span className="text-muted">{t.payment_method}</span>
            <span className="fw-semibold" data-testid="payout-payment-method">{payoutInfo.payment_method}</span>
          </div>
          <div className="d-flex justify-content-between">
            <span className="text-muted">{t.minimum_payout}</span>
            <span className="fw-semibold">{formatCurrency(payoutInfo.minimum_payout)} ₫</span>
          </div>
        </div>

        {!isEditing && (
          <div className="border rounded-3 p-3 mb-3 small" data-testid="payout-bank-account">
            {bankAccount ? (
              <>
                <div className="d-flex justify-content-between mb-1">
                  <span className="text-muted">{t.bank_name}</span>
                  <span className="fw-semibold">{bankAccount.bank_name}</span>
                </div>
                <div className="d-flex justify-content-between mb-1">
                  <span className="text-muted">{t.account_number}</span>
                  <span className="fw-semibold">{bankAccount.account_number}</span>
                </div>
                <div className="d-flex justify-content-between">
                  <span className="text-muted">{t.account_holder}</span>
                  <span className="fw-semibold">{bankAccount.account_holder}</span>
                </div>
              </>
            ) : (
              <span className="text-muted">{t.no_bank_account}</span>
            )}
          </div>
        )}

        {showSaved && (
          <div className="alert alert-success py-2 mb-3" role="alert">
            <small><i className="bi bi-check-circle me-1"></i>{t.saved}</small>
          </div>
        )}

        <div className="alert alert-warning mb-3" role="alert" style={{ fontSize: '12px' }}>
          <i className="bi bi-exclamation-circle me-1"></i>
          <span dangerouslySetInnerHTML={{ __html: warningMessage }} />
        </div>

        <button
          className="btn btn-secondary w-100 mb-2"
          disabled
          style={{ borderRadius: '8px', opacity: 0.6 }}
        >
          <i className="bi bi-download me-2"></i>
          {t.request_payout_disabled}
        </button>

        {isEditing ? (
          <form onSubmit={handleSubmit} data-testid="payout-bank-account-form">
            <div className="mb-2">
              <label className="form-label small text-muted mb-1" htmlFor="payout-bank-name">{t.bank_name}</label>
              <input id="payout-bank-name" className="form-control" value={form.bank_name} onChange={handleChange('bank_name')} required />
            </div>
            <div className="mb-2">
              <label className="form-label small text-muted mb-1" htmlFor="payout-account-number">{t.account_number}</label>
              <input id="payout-account-number" className="form-control" inputMode="numeric" value={form.account_number} onChange={handleChange('account_number')} required />
            </div>
            <div className="mb-3">
              <label className="form-label small text-muted mb-1" htmlFor="payout-account-holder">{t.account_holder}</label>
              <input id="payout-account-holder" className="form-control" value={form.account_holder} onChange={handleChange('account_holder')} required />
            </div>

            {error && (
              <div className="alert alert-danger py-2 mb-3" role="alert">
                <small>{error}</small>
              </div>
            )}

            <div className="d-flex gap-2">
              <button
                type="button"
                className="btn btn-outline-secondary flex-fill"
                style={{ borderRadius: '8px' }}
                onClick={() => setIsEditing(false)}
                disabled={isSaving}
              >
                {t.cancel}
              </button>
              <button
                type="submit"
                className="btn btn-primary flex-fill"
                style={{ borderRadius: '8px' }}
                disabled={isSaving}
              >
                {isSaving && <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>}
                {t.save}
              </button>
            </div>
          </form>
        ) : (
          <button
            className="btn btn-outline-secondary w-100"
            style={{ borderRadius: '8px' }}
            onClick={startEditing}
          >
            {t.update_payment}
          </button>
        )}
      </div>
    </div>
  );
};

export default PayoutInfo;
